import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Chip,
  useTheme
} from '@mui/material'
import {
  Dashboard,
  People,
  Receipt,
  Assessment,
  Settings,
  Business,
  TrendingUp,
  PieChart,
  Notifications,
  ShoppingCart
} from '@mui/icons-material'

import useUIStore from '../../store/uiStore'
import useAuthStore from '../../store/authStore'
import RoleGuard from '../Auth/RoleGuard'

const Sidebar = () => {
  const theme = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const { drawerOpen, drawerWidth, isMobile, setDrawerOpen } = useUIStore()
  const { user, isAdmin } = useAuthStore()

  // Navigation items
  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Dashboard />,
      path: '/dashboard',
      roles: ['ADMINISTRADOR', 'CONSULTA']
    },
    {
      id: 'clients',
      label: 'Clientes',
      icon: <People />,
      path: '/clients',
      roles: ['ADMINISTRADOR', 'CONSULTA']
    },
    {
      id: 'purchase-orders',
      label: 'Órdenes de Compra',
      icon: <ShoppingCart />,
      path: '/purchase-orders',
      roles: ['ADMINISTRADOR', 'CONSULTA']
    },
    {
      id: 'invoices',
      label: 'Facturas',
      icon: <Receipt />,
      path: '/invoices',
      roles: ['ADMINISTRADOR', 'CONSULTA']
    },
    {
      id: 'reports',
      label: 'Reportes',
      icon: <Assessment />,
      path: '/reports',
      roles: ['ADMINISTRADOR', 'CONSULTA']
    }
  ]

  const adminItems = [
    {
      id: 'users',
      label: 'Usuarios',
      icon: <People />,
      path: '/users',
      roles: ['ADMINISTRADOR']
    },
    {
      id: 'notifications',
      label: 'Notificaciones',
      icon: <Notifications />,
      path: '/notifications',
      roles: ['ADMINISTRADOR']
    },
    {
      id: 'settings',
      label: 'Configuración',
      icon: <Settings />,
      path: '/settings',
      roles: ['ADMINISTRADOR']
    }
  ]

  const handleNavigation = (path) => {
    navigate(path)
    setDrawerOpen(false) // Close drawer after navigation on all devices
  }

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard'
    }
    return location.pathname.startsWith(path)
  }

  const renderNavItem = (item) => {
    const active = isActive(item.path)
    
    return (
      <ListItem key={item.id} disablePadding>
        <ListItemButton
          onClick={() => handleNavigation(item.path)}
          selected={active}
          sx={{
            mx: 1,
            my: 0.25,
            borderRadius: 2,
            py: 1,
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main + '20',
              '&:hover': {
                backgroundColor: theme.palette.primary.main + '30',
              },
              '& .MuiListItemIcon-root': {
                color: theme.palette.primary.main,
              },
              '& .MuiListItemText-primary': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              }
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            }
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              color: active ? theme.palette.primary.main : 'text.secondary'
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText 
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.82rem',
              fontWeight: active ? 600 : 500
            }}
          />
        </ListItemButton>
      </ListItem>
    )
  }

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 1.5, pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Business color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700} color="primary.main" fontSize="1.1rem">
            Yotta
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
          Control de Pagos
        </Typography>
      </Box>

      <Divider />

      {/* User info */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="body2" fontWeight={500} fontSize="0.85rem" gutterBottom>
          {user?.name}
        </Typography>
        <Chip 
          label={user?.role === 'ADMINISTRADOR' ? 'Admin' : 'Consulta'}
          size="small"
          color={user?.role === 'ADMINISTRADOR' ? 'primary' : 'default'}
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 20 }}
        />
      </Box>

      <Divider />

      {/* Main navigation */}
      <Box sx={{ flexGrow: 1, py: 1 }}>
        <List>
          {navigationItems.map((item) => (
            <RoleGuard 
              key={item.id}
              allowedRoles={item.roles}
              fallback={null}
            >
              {renderNavItem(item)}
            </RoleGuard>
          ))}
        </List>

        {/* Admin section - Only visible for ADMINISTRADOR role */}
        {isAdmin() && (
          <>
            <Divider sx={{ mx: 2, my: 0.5 }} />
            <Box sx={{ px: 2, py: 0.5 }}>
              <Typography 
                variant="caption" 
                color="text.secondary"
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing={0.5}
                fontSize="0.7rem"
              >
                Administración
              </Typography>
            </Box>
            <List>
              {adminItems.map((item) => renderNavItem(item))}
            </List>
          </>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 1.5, pt: 0.5 }}>
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TrendingUp fontSize="small" color="success" />
          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
            v1.0.0
          </Typography>
        </Box>
      </Box>
    </Box>
  )

  return (
    <>
      {/* Desktop drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen && !isMobile}
        onClose={() => setDrawerOpen(false)}
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: 64, // Height of navbar
            height: 'calc(100vh - 64px)',
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'background.paper',
            boxShadow: 3,
            borderRadius: 0
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen && isMobile}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: 56, // Height of navbar - same as desktop
            height: 'calc(100vh - 64px)', // Same as desktop
            backgroundColor: 'background.paper',
            borderRadius: 0
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  )
}

export default Sidebar
